import {LoaderContext} from 'utilities/loaders/types';
import {LOCALE_STORAGE_KEY} from 'features/Translations/constants';
import {LoaderFunctionArgs} from 'react-router-dom';
import {ShopLocale} from 'utilities/use-shop-locales-and-markets';
import {StatusCode} from '@shopify/network';

import ShopLocalesAndMarketsQuery, {
  ShopLocalesAndMarketsQueryData,
} from './graphql/ShopLocalesAndMarketsQuery.graphql';
import {marketHandleFromLocaleChange} from './utils';

export async function shopLocalesAndMarketsLoader(
  {request}: LoaderFunctionArgs,
  {query}: LoaderContext,
) {
  const {data, error} = await query<ShopLocalesAndMarketsQueryData>({
    query: ShopLocalesAndMarketsQuery,
  });

  if (error) {
    throw new Response(null, {status: StatusCode.InternalServerError});
  }

  const url = new URL(request.url);
  const localeInUrl = url.searchParams.get('shopLocale');
  const marketInUrl = url.searchParams.get('market')
    ? decodeURIComponent(url.searchParams.get('market')!)
    : null;

  const localStorageLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
  const localeInStorage = localStorageLocale
    ? (JSON.parse(localStorageLocale) as ShopLocale)
    : null;

  const locales = data.shopLocales;
  const primaryLocale = locales.find((locale) => locale.primary)!;
  const firstAlternateLocale = locales.find((locale) => !locale.primary);
  const currentLocaleCode =
    localeInUrl ||
    localeInStorage?.locale ||
    firstAlternateLocale?.locale ||
    primaryLocale.locale;
  const currentLocale =
    locales.find(({locale}) => locale === currentLocaleCode) ||
    firstAlternateLocale ||
    primaryLocale;

  const markets = data.markets.map((market) => ({
    ...market,
    webPresences: market.webPresences.map((webPresence) => ({
      ...webPresence,
      alternateLocales: webPresence.alternateLocales.map(
        (alternateLocale) => alternateLocale.locale,
      ),
      defaultLocale: webPresence.defaultLocale.locale,
    })),
  }));

  const {marketHandle: newMarketHandle} = marketHandleFromLocaleChange(
    markets,
    currentLocaleCode,
    marketInUrl,
  );

  const currentMarket = (() => {
    const market = newMarketHandle
      ? markets.find(({handle}) => handle === newMarketHandle)
      : undefined;

    if (currentLocale.primary && !market) {
      return markets[0];
    }

    return market;
  })();

  return {
    shopLocalesAndMarkets: {
      primaryLocale,
      currentLocale,
      currentMarket,
      locales,
      markets,
    },
  };
}
